import { type ReactNode, useId, type Ref } from 'react'
import {
  Card,
  CardItemContent,
  CardItemIcon,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import cx from 'classnames'
import styles from './RequestedDocumentCard.module.css'
import { IconButton } from '@/components/IconButton'
import { useFileTrigger } from '@/hooks/file-hooks'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { Button } from '@/components/Button'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import { GridList, GridListItem } from 'react-aria-components'
import { Spinner } from '@/components/Spinner'
import { CircularProgressbar } from '@/components/CircularProgressbar'
import {
  AutoSkeleton,
  SkeletonColorContext,
  useIsSkeleton,
  useSkeletonListContext,
} from '@/components/Skeleton'

export interface RequestedDocumentCardProps
  extends RequestedDocumentCardActionsProps {
  title: string
  description?: string
  children?: ReactNode
  variant?: 'default' | 'decorative'
  testId?: string
  triggerTestId?: string
  uploadNewTestId?: string
  linkExistingTestId?: string
}

/**
 * The RequestedDocumentCard
 */
export function RequestedDocumentCard({
  title,
  description,
  isDisabled,
  onUploadNew,
  onLinkExisting,
  children,
  variant = 'default',
  testId,
  triggerTestId,
  uploadNewTestId,
  linkExistingTestId,
}: RequestedDocumentCardProps) {
  const titleId = useId()
  return (
    <Card testId={testId}>
      <CardItemContent
        align="center"
        spacingY="lg"
        icon={variant === 'decorative' && <CardItemIcon name="upload" />}
        extras={
          <RequestedDocumentCardActions
            isDisabled={isDisabled}
            onUploadNew={onUploadNew}
            onLinkExisting={onLinkExisting}
            triggerTestId={triggerTestId}
            uploadNewTestId={uploadNewTestId}
            linkExistingTestId={linkExistingTestId}
          />
        }
      >
        <CardTitle id={titleId}>{title}</CardTitle>
        <CardSubtitle variant="sm-description">{description}</CardSubtitle>
      </CardItemContent>
      <GridList aria-labelledby={titleId}>{children}</GridList>
    </Card>
  )
}

interface RequestedDocumentCardActionsProps {
  /**
   * Whether the control is disabled.
   */
  isDisabled?: boolean
  /**
   * Called when the user wants to upload a new document.
   */
  onUploadNew?: (files: File[]) => unknown
  /**
   * Called when the user wants to link an existing document.
   */
  onLinkExisting?: () => unknown
  /**
   * Test ID attribute for the action button that shows the dropdown.
   */
  triggerTestId?: string
  /**
   * Test ID attribute for the "upload new" action in the dropdown.
   */
  uploadNewTestId?: string
  /**
   * * Test ID attribute for the "link existing" action in the dropdown.
   */
  linkExistingTestId?: string
}

/**
 * Actions for a {@link RequestedDocumentCard}.
 */
export function RequestedDocumentCardActions({
  onUploadNew,
  onLinkExisting,
  isDisabled,
  triggerTestId,
  uploadNewTestId,
  linkExistingTestId,
}: RequestedDocumentCardActionsProps) {
  const isSpacious = useIsSpacious()
  const onUploadPress = useFileTrigger({
    allowMultiple: true,
    onFilesSelected: onUploadNew ?? (() => void 0),
  })

  if (!isSpacious) {
    return onLinkExisting ? (
      <Dropdown
        trigger={
          <IconButton
            isDisabled={isDisabled}
            label="Select file"
            name="upload"
            testId={triggerTestId}
          />
        }
        title="Actions"
      >
        <DropdownItem
          icon="upload"
          onAction={onUploadPress}
          testId={uploadNewTestId}
        >
          Upload
        </DropdownItem>
        <DropdownItem
          icon="link"
          onAction={onLinkExisting}
          testId={linkExistingTestId}
        >
          Link existing
        </DropdownItem>
      </Dropdown>
    ) : (
      <IconButton
        isDisabled={isDisabled}
        label="Select file"
        name="upload"
        testId={uploadNewTestId}
        onPress={onUploadPress}
      />
    )
  }

  return (
    <div className={styles.cardActions}>
      <Button
        isDisabled={isDisabled}
        label="Upload"
        variant="text-inline"
        onPress={onUploadPress}
        testId={uploadNewTestId}
      />
      {onLinkExisting && (
        <>
          <span className={styles.cardActionsSeparator}>|</span>
          <Button
            isDisabled={isDisabled}
            label="Link"
            variant="text-inline"
            onPress={onLinkExisting}
            testId={linkExistingTestId}
          />
        </>
      )}
    </div>
  )
}

interface RequestedDocumentFileListItemProps {
  fileName: string
  children?: ReactNode
  isDisabled?: boolean
  isSkeleton?: boolean
  /**
   * If specified, an upload progress indicator will be rendered instead
   * of the remove button. When a number, should be between 0 and 100. When
   * `indefinite`, a regular spinner will be rendered
   */
  progress?: number | 'indefinite'
  /**
   * Called when the user wishes to remove the file.
   */
  onRemovePress?: () => unknown
  /**
   * Test ID attribute for the remove button.
   */
  removeButtonTestId?: string
}

/**
 * Requested Document File List Item
 */
export function RequestedDocumentFileListItem({
  fileName,
  progress,
  onRemovePress,
  isSkeleton,
  isDisabled,
  removeButtonTestId,
}: RequestedDocumentFileListItemProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  const { vary, ref } = useSkeletonListContext()

  let extras = onRemovePress && (
    <RequestedDocumentFileListItemRemoveButton
      isDisabled={isDisabled}
      onPress={onRemovePress}
      testId={removeButtonTestId}
    />
  )

  if (typeof progress === 'number') {
    extras = (
      <div className={styles.fileStatus}>
        <CircularProgressbar size={24} thickness={3} progress={progress} />
      </div>
    )
  }

  if (progress === 'indefinite') {
    extras = (
      <div className={styles.fileStatus}>
        <Spinner size={24} thickness={3} />
      </div>
    )
  }

  if (isSkeleton) {
    extras = (
      <div className={styles.fileStatus}>
        <IconButton isDisabled name="close" label="Placeholder" isSkeleton />
      </div>
    )
  }

  return (
    <GridListItem
      ref={ref as Ref<HTMLDivElement>}
      textValue={fileName}
      isDisabled={isDisabled}
      className={({ isFocusVisible }) =>
        cx(styles.fileListItem, isFocusVisible && styles.focused)
      }
    >
      <SkeletonColorContext value="secondary">
        <p className={styles.fileName}>
          <AutoSkeleton isSkeleton={isSkeleton} characters={vary(24)}>
            {fileName}
          </AutoSkeleton>
        </p>
        {extras && <div className={styles.fileListItemExtras}>{extras}</div>}
      </SkeletonColorContext>
    </GridListItem>
  )
}

/**
 * Requested Document File List Skeleton Item
 */
export function RequestedDocumentFileListSkeletonItem() {
  return <RequestedDocumentFileListItem fileName="Placeholder" isSkeleton />
}

interface RequestedDocumentFileListItemRemoveButtonProps {
  onPress?: () => unknown
  isDisabled?: boolean
  testId?: string
}

/**
 * Requested Document File List Item Remove Button
 */
function RequestedDocumentFileListItemRemoveButton({
  onPress,
  isDisabled,
  testId,
}: RequestedDocumentFileListItemRemoveButtonProps) {
  const isSpacious = useIsSpacious()
  if (!isSpacious) {
    return (
      <IconButton
        isDisabled={isDisabled}
        name="close"
        variant="default"
        label="Remove"
        onPress={onPress}
        testId={testId}
      />
    )
  }

  return (
    <Button
      label="Remove"
      onPress={onPress}
      variant="text"
      isDisabled={isDisabled}
      testId={testId}
    />
  )
}

/**
 * Requested Document Footnote
 */
export function RequestedDocumentFootnote() {
  return (
    <p className={styles.footnote}>
      Files must be smaller than 10MB and should ideally be one of the following
      file types: CSV, DOC, DOCX, JPG, JPEG, PDF, PNG, XLS, XLSX
    </p>
  )
}
